import { FC, MouseEvent, ReactElement, useEffect } from 'react'
import { Keyboard, Navigation, Pagination, Zoom } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import { Button, Icon } from '@/components'
import { PacoModal } from '@/compositions/PacoModal/PacoModal'
import { trans } from '@/helpers/trans'

import './ImageViewer.scss'

interface ImageViewerProps {
    images: string[]
    initialIndex?: number
    onClose: () => void
}

const isDismissBlockingElement = (element: HTMLElement): boolean => Boolean(
    element.closest(
        '.image-viewer__image, .image-viewer__close, .swiper-button-next, .swiper-button-prev, .swiper-pagination',
    ),
)

export const ImageViewer: FC<ImageViewerProps> = ({ images, initialIndex = 0, onClose }): ReactElement => {
    const handleModalClick = (event: MouseEvent<HTMLElement>): void => {
        if (event.target instanceof HTMLElement && isDismissBlockingElement(event.target)) return

        onClose()
    }

    useEffect((): () => void => {
        const handleKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') onClose()
        }

        document.addEventListener('keydown', handleKeyDown)

        return (): void => document.removeEventListener('keydown', handleKeyDown)
    }, [onClose])

    return (
        <PacoModal className="image-viewer" cardClassName="image-viewer__card" onModalClick={handleModalClick}>
            <Button
                type="button"
                text={trans('common.close')}
                aria-label={trans('common.close')}
                className="image-viewer__close"
                onClick={onClose}
            >
                <Icon name="cross-close" className="image-viewer__close-icon" />
            </Button>

            <Swiper
                modules={[Keyboard, Navigation, Pagination, Zoom]}
                zoom
                keyboard
                navigation={images.length > 1}
                pagination={images.length > 1 ? { type: 'fraction' } : false}
                initialSlide={initialIndex}
                spaceBetween={16}
                className="image-viewer__swiper"
            >
                {images.map(image => (
                    <SwiperSlide key={image} className="image-viewer__slide">
                        <div className="swiper-zoom-container">
                            <img src={image} alt="" className="image-viewer__image" />
                        </div>
                    </SwiperSlide>
                ))}
            </Swiper>
        </PacoModal>
    )
}
